import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  Tabs,
  Tab,
  IconButton,
  Divider,
} from "@mui/material";
import { styled } from "@mui/system";
import {
  Edit,
  Pause,
  Cancel,
  Refresh,
  SwapHoriz,
  Print,
  ArrowBackIosNew,
} from "@mui/icons-material";
import { TabsWrapper } from "../../../style";

const HeaderWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
  textTransform: "none",
  borderRadius: "8px",
  fontWeight: 600,
  padding: "6px 16px",
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  marginBottom: theme.spacing(0.5),
  letterSpacing: "0.05em",
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "1rem",
  color: theme.palette.text.primary,
}));

const TabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 0),
}));

const PolicyDataGrid = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const Polizze = () => {
  const [activeSubTab, setActiveSubTab] = useState(0);

  const samplePolicy = {
    numeroPolizza: "UNI-2025-00201",
    stato: "Attiva",
    compagnia: "UnipolSai Assicurazioni",
    ramo: "RC Autoveicoli Terrestri",
    prodotto: "RCA Top",
    contraente: "Russo Angela",
    produttore: "Giulia Ferrara",
    dataEffetto: "15/01/2025",
    dataScadenza: "15/01/2026",
    frazionamento: "annuale",
    premioLordo: "€780,00",
    premioNetto: "€640,00",
    imposte: "€140,00",
    provvAgenzia: "€96,00",
    provvProduttore: "€48,00",
    tacitoRinnovo: "Sì",
    targa: "NA211PL",
    beneAssicurato: "Toyota Yaris 1.0",
  };

  const handleSubTabChange = (event, newValue) => {
    setActiveSubTab(newValue);
  };

  return (
    <Box>
      <Box
        sx={{
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          overflow: "hidden",
        }}
      >
        <Box p={4}>
          {/* Header with Back button, Policy number and Status */}
          <HeaderWrapper>
            <IconButton size="small" sx={{ mr: 2, width: 32, height: 32 }}>
              <ArrowBackIosNew sx={{ fontSize: 14 }} />
            </IconButton>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mr: 1, fontWeight: 500 }}
            >
              Polizze
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, mr: 2, letterSpacing: "-0.02em" }}
            >
              {samplePolicy.numeroPolizza}
            </Typography>
            <Chip
              label={samplePolicy.stato}
              sx={{
                bgcolor: "#e8f5e9",
                color: "#2e7d32",
                fontWeight: 700,
                borderRadius: "6px",
                height: 24,
                fontSize: "0.75rem",
              }}
            />
          </HeaderWrapper>

          {/* Sub-header info */}
          <Box mb={3}>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontWeight: 500 }}
            >
              {samplePolicy.contraente} • {samplePolicy.compagnia} •{" "}
              {samplePolicy.ramo}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box mb={4} display="flex" flexWrap="wrap" gap={1}>
            <ActionButton
              variant="outlined"
              startIcon={<Edit sx={{ fontSize: 18 }} />}
              sx={{
                "&:hover": {
                  bgcolor: "primary.main",
                  color: "white",
                  borderColor: "primary.main",
                },
              }}
            >
              Modifica
            </ActionButton>
            <ActionButton
              variant="outlined"
              startIcon={<Pause sx={{ fontSize: 18 }} />}
              sx={{
                color: "#ed6c02",
                borderColor: "#ed6c02",
                "&:hover": {
                  bgcolor: "#ed6c02",
                  color: "white",
                  borderColor: "#ed6c02",
                },
              }}
            >
              Sospendi
            </ActionButton>
            <ActionButton
              variant="outlined"
              startIcon={<Cancel sx={{ fontSize: 18 }} />}
              sx={{
                color: "#d32f2f",
                borderColor: "#d32f2f",
                "&:hover": {
                  bgcolor: "#d32f2f",
                  color: "white",
                  borderColor: "#d32f2f",
                },
              }}
            >
              Annulla
            </ActionButton>
            <ActionButton
              variant="outlined"
              startIcon={<Refresh sx={{ fontSize: 18 }} />}
              sx={{
                color: "#0288d1",
                borderColor: "#0288d1",
                "&:hover": {
                  bgcolor: "#0288d1",
                  color: "white",
                  borderColor: "#0288d1",
                },
              }}
            >
              Rinnova
            </ActionButton>
            <ActionButton
              variant="outlined"
              startIcon={<SwapHoriz sx={{ fontSize: 18 }} />}
              sx={{
                color: "#9c27b0",
                borderColor: "#9c27b0",
                "&:hover": {
                  bgcolor: "#9c27b0",
                  color: "white",
                  borderColor: "#9c27b0",
                },
              }}
            >
              Sostituisci
            </ActionButton>
            <ActionButton
              variant="outlined"
              startIcon={<Print sx={{ fontSize: 18 }} />}
              sx={{
                "&:hover": {
                  bgcolor: "primary.main",
                  color: "white",
                  borderColor: "primary.main",
                },
              }}
            >
              Stampa
            </ActionButton>
          </Box>

          {/* Sub Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <TabsWrapper>
              <Tabs
                value={activeSubTab}
                onChange={handleSubTabChange}
                aria-label="policy sub tabs"
              >
                <Tab label="Dati Polizza" />
                <Tab label="Titoli" />
                <Tab label="App Preview" />
                <Tab label="Sinistri" />
                <Tab label="Movimenti" />
                <Tab label="Documenti" />
              </Tabs>
            </TabsWrapper>
          </Box>

          {/* Tab Panel Content (Dati Polizza) */}
          {activeSubTab === 0 && (
            <TabPanel>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Box mb={3}>
                    <InfoLabel>N° POLIZZA</InfoLabel>
                    <InfoValue>{samplePolicy.numeroPolizza}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>PRODOTTO</InfoLabel>
                    <InfoValue>{samplePolicy.prodotto}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>DATA EFFETTO</InfoLabel>
                    <InfoValue>{samplePolicy.dataEffetto}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>PREMIO LORDO</InfoLabel>
                    <InfoValue>{samplePolicy.premioLordo}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>PROVV. AGENZIA</InfoLabel>
                    <InfoValue>{samplePolicy.provvAgenzia}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>TARGA</InfoLabel>
                    <InfoValue>{samplePolicy.targa}</InfoValue>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box mb={3}>
                    <InfoLabel>COMPAGNIA</InfoLabel>
                    <InfoValue>{samplePolicy.compagnia}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>CONTRAENTE</InfoLabel>
                    <InfoValue>{samplePolicy.contraente}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>DATA SCADENZA</InfoLabel>
                    <InfoValue>{samplePolicy.dataScadenza}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>PREMIO NETTO</InfoLabel>
                    <InfoValue>{samplePolicy.premioNetto}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>PROVV. PRODUTTORE</InfoLabel>
                    <InfoValue>{samplePolicy.provvProduttore}</InfoValue>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box mb={3}>
                    <InfoLabel>RAMO</InfoLabel>
                    <InfoValue>{samplePolicy.ramo}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>PRODUTTORE</InfoLabel>
                    <InfoValue>{samplePolicy.produttore}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>FRAZIONAMENTO</InfoLabel>
                    <InfoValue>{samplePolicy.frazionamento}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>IMPOSTE</InfoLabel>
                    <InfoValue>{samplePolicy.imposte}</InfoValue>
                  </Box>
                  <Box mb={3}>
                    <InfoLabel>TACITO RINNOVO</InfoLabel>
                    <InfoValue>{samplePolicy.tacitoRinnovo}</InfoValue>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ p: 3, borderRadius: "8px", mt: 2 }}>
                <InfoLabel>BENE ASSICURATO</InfoLabel>
                <InfoValue sx={{ fontWeight: 600 }}>
                  {samplePolicy.beneAssicurato}
                </InfoValue>
              </Box>
            </TabPanel>
          )}

          {activeSubTab !== 0 && (
            <Box p={10} textAlign="center">
              <Typography color="textSecondary" sx={{ fontStyle: "italic" }}>
                Sezione "
                {
                  [
                    "Dati Polizza",
                    "Titoli",
                    "App Preview",
                    "Sinistri",
                    "Movimenti",
                    "Documenti",
                  ][activeSubTab]
                }
                " in fase di sviluppo
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Polizze;
